import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Activity, Brain, Trash2, Clock, TrendingUp } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * Memory-04 — /memory-health
 * KPIs de saturação, decay, consolidação. Admin-only via RoleGate no App.tsx.
 */
export default function MemoryHealth() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["memory-health-stats"],
    queryFn: async () => {
      const [byLayer, ltm, forgotten, lastRun, confDist] = await Promise.all([
        supabase.from("consolidated_memories")
          .select("memory_layer", { count: "exact", head: false })
          .eq("active", true),
        supabase.from("ltm_canonical").select("id", { count: "exact", head: true }).is("effective_until", null),
        supabase.from("memory_forgotten").select("id", { count: "exact", head: true }).eq("restored", false),
        supabase.from("memory_consolidation_runs").select("*").order("created_at", { ascending: false }).limit(5),
        supabase.from("consolidated_memories").select("confidence").eq("active", true).limit(2000),
      ]);
      const layerCounts: Record<string, number> = {};
      for (const r of byLayer.data ?? []) layerCounts[r.memory_layer] = (layerCounts[r.memory_layer] ?? 0) + 1;
      const buckets = [0, 0, 0, 0, 0]; // 0-20, 20-40, 40-60, 60-80, 80-100
      for (const r of confDist.data ?? []) {
        const c = r.confidence ?? 0;
        const b = Math.min(4, Math.floor(c * 5));
        buckets[b]++;
      }
      return {
        layerCounts,
        ltmCount: ltm.count ?? 0,
        forgottenCount: forgotten.count ?? 0,
        runs: lastRun.data ?? [],
        buckets,
        totalActive: confDist.data?.length ?? 0,
      };
    },
  });

  if (isLoading) return <div className="container mx-auto p-6"><Skeleton className="h-96" /></div>;

  const lr = stats?.runs[0];

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Saúde da memória</h1>
        <p className="text-sm text-muted-foreground">KPIs internos de consolidação, decay e esquecimento.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Kpi icon={<Brain />} label="Recente / Padrões" value={String((stats?.layerCounts.episodic ?? 0) + (stats?.layerCounts.semantic ?? 0))} />
        <Kpi icon={<TrendingUp />} label="Oficial (LTM)" value={String(stats?.ltmCount ?? 0)} />
        <Kpi icon={<Trash2 />} label="Esquecidas (janela 30d)" value={String(stats?.forgottenCount ?? 0)} />
        <Kpi icon={<Clock />} label="Última consolidação" value={lr ? formatDistanceToNow(new Date(lr.created_at), { locale: ptBR, addSuffix: true }) : "—"} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-sm">Distribuição de força (memórias ativas: {stats?.totalActive})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {["Muito fraca (0–20%)","Fraca (20–40%)","Média (40–60%)","Forte (60–80%)","Muito forte (80–100%)"].map((label, i) => {
            const v = stats?.buckets[i] ?? 0;
            const pct = stats?.totalActive ? Math.round((v / stats.totalActive) * 100) : 0;
            return (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="w-44">{label}</span>
                <Progress value={pct} className="h-2 flex-1" />
                <span className="w-16 text-right text-muted-foreground">{v} ({pct}%)</span>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Últimas execuções de consolidação</CardTitle></CardHeader>
        <CardContent>
          {!stats?.runs.length ? (
            <p className="text-sm text-muted-foreground">Nenhuma execução registrada ainda.</p>
          ) : (
            <ul className="text-sm space-y-2">
              {stats.runs.map((r: any) => (
                <li key={r.id} className="flex justify-between border-b pb-2 last:border-0">
                  <div>
                    <div>{formatDistanceToNow(new Date(r.created_at), { locale: ptBR, addSuffix: true })}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.candidates_seen} vistos · {r.merged} criados · {r.reinforced} reforçados · {r.decayed} enfraquecidos
                      {r.details?.dry_run && <Badge variant="outline" className="ml-2">shadow</Badge>}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{r.duration_ms ?? "?"} ms</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Como a memória funciona</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2 text-muted-foreground">
          <p>A IA observa mensagens e arquiva o que entende em camadas: <b>Recente</b> (segundos a horas), <b>Padrões</b> (dias a semanas) e <b>Oficial</b> (regras do negócio que ela respeita).</p>
          <p>Memórias enfraquecem com o tempo se ninguém as usa (curva de esquecimento). Memórias acessadas ou reforçadas ficam mais fortes.</p>
          <p>Quando o mesmo padrão aparece várias vezes em mensagens diferentes, a IA pode juntar tudo num "padrão" único — isso é a <b>consolidação semântica</b>.</p>
          <p className="text-xs">Inspirado em modelos clássicos de memória (Tulving, McClelland CLS, Murre-Dros, Karpicke-Roediger).</p>
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          <span className="w-4 h-4 [&_svg]:w-4 [&_svg]:h-4">{icon}</span>{label}
        </div>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}
